'use client';

import { ReactNode, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Info } from 'lucide-react';
import Image from 'next/image';
import { PhaseStepper } from './PhaseStepper';
import { ContextDrawer } from './ContextDrawer';
import type { NoteTakerAnalysis, InterviewFlowType, TopicDefinition, PhaseId } from '@/lib/interview/types';
import { withBase } from '@/lib/base-path'

interface ChatInterfaceLayoutProps {
  sessionId: string;
  flowType: InterviewFlowType;
  topics: TopicDefinition[];
  analysis: NoteTakerAnalysis | null;
  currentTopicId?: string | null;
  currentPhase?: PhaseId | null;
  title: string;
  subtitle?: string;
  isCompleting?: boolean;
  turnProgressInPhase?: number;
  coveredAreas?: Partial<Record<PhaseId, string[]>>;
  children: ReactNode;
}

export function ChatInterfaceLayout({
  topics,
  analysis,
  currentTopicId,
  currentPhase,
  title,
  subtitle,
  isCompleting = false,
  turnProgressInPhase,
  coveredAreas,
  children,
}: ChatInterfaceLayoutProps) {
  const router = useRouter();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const activePhase: PhaseId = currentPhase || analysis?.phaseAssessment?.currentPhase || 'discovery';

  return (
    <div className="flex flex-col h-screen bg-off-white overflow-hidden">
      {/* Header: brand, conversation steps, details */}
      <header className="flex-shrink-0 px-4 pt-3 pb-4 border-b border-off-black/10 bg-off-white/95 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowLeaveModal(true)}
                className="p-2 -ml-2 rounded-xl hover:bg-off-black/5 transition-colors group"
                aria-label="Terug naar home"
              >
                <ArrowLeft className="w-5 h-5 text-off-black/50 group-hover:text-off-black/70 transition-colors" />
              </button>
              <Image src={withBase("/logomark.svg")} alt="" width={22} height={18} className="h-[18px] w-auto" />
              <span className="text-sm font-medium text-off-black">AI Discovery</span>
              <span className="sr-only">: {title}</span>
              {subtitle && (
                <span className="hidden sm:inline text-sm text-off-black/50">
                  {subtitle}
                </span>
              )}
            </div>

            <button
              onClick={() => setIsDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-sm text-off-black/60 hover:text-off-black hover:bg-off-black/5 transition-colors"
              aria-label="Gespreksdetails bekijken"
            >
              <Info className="w-4 h-4" />
              <span className="hidden sm:inline">Wat we tot nu toe weten</span>
            </button>
          </div>

          <div className="mt-3">
            <PhaseStepper
              currentPhase={activePhase}
              progressInPhase={turnProgressInPhase}
              isCompleting={isCompleting}
              coveredAreas={coveredAreas}
            />
          </div>
        </div>
      </header>

      {/* Chat Content - full height, scrollable */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-3xl mx-auto w-full h-full">
          {children}
        </div>
      </div>

      {/* Context Drawer - on-demand */}
      <ContextDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        analysis={analysis}
        topics={topics}
        currentTopicId={currentTopicId}
        currentPhase={currentPhase}
      />

      {/* Leave Confirmation Dialog */}
      {showLeaveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leave-modal-title"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowLeaveModal(false);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLeaveModal(false);
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-sm mx-4 p-6">
            <h3 id="leave-modal-title" className="font-display font-light text-2xl text-off-black mb-2">
              Gesprek verlaten?
            </h3>
            <p className="text-sm text-off-black/60 mb-6">
              Je voortgang wordt bewaard en je kunt later verdergaan.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLeaveModal(false)}
                className="px-4 py-2 text-sm rounded-xl border border-off-black/15 hover:bg-off-black/5 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={() => router.push('/')}
                className="px-4 py-2 text-sm rounded-xl bg-orange-cta hover:bg-orange-hover text-white transition-colors"
              >
                Verlaten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
